import { useMemo, useState } from "react";
import { Button } from "@/components/Button/Button";
import { useStripe } from "@/hooks/useStripe";

interface PromoCodeInputProps {
    paymentIntentId: string | null;
    setActivePromoCode: (promoCode: string) => void;
    activePromoCode: string | null;
    setDiscountAmount: (discountAmount: number) => void;
}

export const PromoCodeInput = ({
    paymentIntentId,
    setActivePromoCode,
    activePromoCode,
    setDiscountAmount,
}: PromoCodeInputProps) => {
    const [promoCode, setPromoCode] = useState("");
    const { validatePromoCode, updatePaymentIntent } = useStripe();
    const [isLoading, setIsLoading] = useState(false);

    const canSubmit = useMemo(() => {
        return (
            promoCode.trim() &&
            activePromoCode?.toLowerCase() !== promoCode.toLowerCase()
        );
    }, [promoCode, activePromoCode]);

    const handleApplyPromoCode = async () => {
        if (promoCode.trim()) {
            if (activePromoCode === promoCode) {
                setActivePromoCode("");
                setPromoCode("");
                return;
            }

            setIsLoading(true);
            const response = await validatePromoCode(
                promoCode,
                paymentIntentId!,
            );

            if (!response?.newAmount) {
                setPromoCode("");
                setIsLoading(false);
                return;
            }

            await updatePaymentIntent(paymentIntentId!, response.newAmount, {
                promoCode: promoCode.toLowerCase(),
                discount: response?.discount,
                originalAmount: response?.originalAmount,
            });

            const updatedAmount = response?.newAmount;
            if (updatedAmount) {
                setActivePromoCode(promoCode);
                setDiscountAmount(response?.discount);
                setPromoCode("");
            }
            setIsLoading(false);
        }
    };

    if (!paymentIntentId) {
        return null;
    }

    return (
        <div className="flex gap-4 pb-3">
            <input
                type="text"
                value={promoCode}
                onChange={e => setPromoCode(e.target.value)}
                onKeyDown={e => {
                    if (e.key === "Enter" && canSubmit) {
                        handleApplyPromoCode();
                    }
                }}
                placeholder="Enter promo code"
                className="text-md h-9 flex-1 rounded-md border-none bg-neutral-50 px-3 py-2 text-neutral-900 shadow-sm ring-0 placeholder:text-neutral-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-neutral-200"
            />
            <Button
                onClick={handleApplyPromoCode}
                size="default"
                loading={isLoading}
                disabled={!canSubmit}
            >
                Apply
            </Button>
        </div>
    );
};
